import { ShieldOff } from 'lucide-react';

/**
 * GuardrailsOffDisclosure
 *
 * Shown whenever a low-confidence numeric estimate is rendered ONLY because
 * the user has flipped the global Guardrails toggle to OFF (Indicative Report
 * Mode). The disclosure is intentionally:
 *
 *   - prominent (full-width amber band, ShieldOff icon)
 *   - explicit ("Visible because Guardrails are OFF — this low-confidence
 *     estimate would normally be hidden until additional confirmation is
 *     available.")
 *   - not dismissable
 *
 * Used inside `SampleJumpAnalysisPanel`, `FCRAnalysisPanel`, and the
 * Planning Estimate "Show raw supporting figure" branch so the same
 * language appears wherever a number is exposed under the override.
 *
 * Props:
 *   variant   'short' | 'full'   default 'full'.
 *                                'short' is a compact one-liner used inside
 *                                tight grid cells (e.g. next to a single
 *                                Estimated-Missing value).
 *   testId    optional override for the data-testid.
 */
export default function GuardrailsOffDisclosure({ variant = 'full', testId = 'guardrails-off-disclosure' }) {
  const isShort = variant === 'short';

  if (isShort) {
    return (
      <p
        className="text-[10px] text-amber-800 leading-snug font-medium"
        data-testid={`${testId}-short`}
      >
        Guardrails OFF: low-confidence estimate shown for reference only.
      </p>
    );
  }

  return (
    <div
      className="flex items-start gap-2 px-3 py-2 rounded-md bg-amber-50 border border-amber-300 text-amber-900"
      data-testid={testId}
      role="note"
    >
      <ShieldOff className="h-4 w-4 mt-0.5 flex-shrink-0 text-amber-700" />
      <div className="text-xs leading-snug">
        <span className="font-semibold">Guardrails OFF: low-confidence estimate shown for reference only.</span>{' '}
        Visible because Guardrails are OFF — this figure would normally be hidden until additional confirmation is available.
        It is indicative and should not be used for operational stock adjustment without independent validation.
      </div>
    </div>
  );
}
